/**
 * AICoachPage - Platform Integration (F11)
 * Q AI Coach page for existing QTradeView platform users
 *
 * Route: /ai-coach (protected - requires platform login)
 * Uses existing auth context and integrates with platform navigation
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { Box, Container } from '@mui/material';
import { useAuth } from '../provider/authProvider';
import useQChat from '../hooks/useQChat';
import QChatWindow from '../Components/QAI/QChatWindow';
import PricingModal from '../Components/QAI/PricingModal';

/**
 * AICoachPage Component
 * Full-screen Q AI chat interface for platform users
 */
const AICoachPage = () => {
  const { token, getCurrentUser } = useAuth();
  const user = getCurrentUser();
  const userName = user?.firstName || 'Trader';

  // Initialize Q Chat hook
  const {
    messages,
    balance,
    isLoading,
    showPricingModal,
    error,
    isLowBalance,
    canSend,
    sendMessage,
    handleActionCard,
    refreshBalance,
    cancelStream,
    clearMessages,
    closePricingModal
  } = useQChat(token, userName);

  // Ref to track if balance has been fetched
  const balanceFetched = useRef(false);

  // Fetch balance on mount
  useEffect(() => {
    if (token && !balanceFetched.current) {
      balanceFetched.current = true;
      refreshBalance();
    }
  }, [token, refreshBalance]);

  // Handle buy tokens click
  const handleBuyTokens = useCallback(() => {
    // For now, just show the pricing modal
    // In production, this would initiate payment flow
    closePricingModal();
    // Re-open after close to show modal
    setTimeout(() => {
      // This is a workaround since we don't have setShowPricingModal exposed
      // In production, you'd have a dedicated openPricingModal function
    }, 0);
  }, [closePricingModal]);

  // Handle tier selection from pricing modal
  const handleSelectTier = useCallback(async (tierId) => {
    // TODO: Integrate with payment flow (Payoneer/Coinbase)
    console.log('Selected tier:', tierId);
    // For now, just close the modal
    // In production: redirect to payment gateway
    closePricingModal();
  }, [closePricingModal]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#0A0A0F',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Main Chat Container - PHASE 4: Wider layout for data density */}
      <Container
        maxWidth="xl"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          py: { xs: 2, md: 3 },
          px: { xs: 1, md: 2 }
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0, // Important for flex children to scroll
            height: 'calc(100vh - 48px)' // Account for padding
          }}
        >
          <QChatWindow
            messages={messages}
            balance={balance}
            isLoading={isLoading}
            isLowBalance={isLowBalance}
            canSend={canSend}
            onSendMessage={sendMessage}
            onActionCard={handleActionCard}
            onCancelStream={cancelStream}
            onBuyTokens={handleBuyTokens}
            onClearChat={clearMessages}
            error={error}
          />
        </Box>
      </Container>

      {/* Pricing Modal */}
      <PricingModal
        open={showPricingModal}
        onClose={closePricingModal}
        onSelectTier={handleSelectTier}
        currentBalance={balance}
      />
    </Box>
  );
};

export default AICoachPage;
